import React, { useState, useEffect } from 'react';
import { dealerService, mediaService } from '../../services/api';
import { Plus, Search, Edit2, Trash2, X, MapPin, Phone, Mail, Percent, ShieldCheck, ShieldAlert, Users, Navigation, Globe, Image as ImageIcon, ExternalLink, Eye } from 'lucide-react';

function DealersManagement() {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geocoding, setGeocoding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [viewingDealer, setViewingDealer] = useState(null);
  const [editingDealer, setEditingDealer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    latitude: '',
    longitude: '',
    website: '',
    commission_rate: '',
    status: 'active',
    is_featured: false
  });

  useEffect(() => {
    loadDealers();
  }, []);

  const loadDealers = async () => {
    setLoading(true);
    try {
      const data = await dealerService.index();
      setDealers(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error('Error loading dealers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoLocate = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData(prev => ({
          ...prev,
          latitude: position.coords.latitude.toFixed(8),
          longitude: position.coords.longitude.toFixed(8)
        }));
      },
      () => {
        alert('Unable to retrieve your location. Please check browser permissions.');
      }
    );
  };

  const handleGeocode = async () => {
    const { address, city, state, postal_code } = formData;
    const query = `${address} ${city} ${state} ${postal_code}`.trim();

    if (!query) {
      alert('Please enter an address or city first to geocode.');
      return;
    }

    setGeocoding(true);
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`);
      const data = await response.json();
      
      if (data && data[0]) {
        setFormData(prev => ({
          ...prev,
          latitude: parseFloat(data[0].lat).toFixed(8),
          longitude: parseFloat(data[0].lon).toFixed(8)
        }));
      } else {
        alert('Could not find coordinates for this address. Please try refining the address.');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      alert('Error fetching coordinates. Please try manual entry.');
    } finally {
      setGeocoding(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const res = await mediaService.upload(file, 'dealers');
      setFormData(prev => ({ ...prev, logo: res.url }));
    } catch (error) {
      console.error('Logo upload error:', error);
      alert('Failed to upload logo.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDealer) {
        await dealerService.update(editingDealer.id, formData);
      } else {
        await dealerService.store(formData);
      }
      closeModal();
      loadDealers();
    } catch (error) {
      alert('Error saving dealer: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (dealer) => {
    setEditingDealer(dealer);
    setFormData({
      name: dealer.name || '',
      logo: dealer.logo || '',
      email: dealer.email || '',
      phone: dealer.phone || '',
      address: dealer.address || '',
      city: dealer.city || '',
      state: dealer.state || '',
      postal_code: dealer.postal_code || '',
      latitude: dealer.latitude || '',
      longitude: dealer.longitude || '',
      website: dealer.website || '',
      commission_rate: dealer.commission_rate || '',
      status: dealer.status || 'active',
      is_featured: dealer.is_featured || false
    });
    setShowModal(true);
  };

  const handleView = (dealer) => {
    setViewingDealer(dealer);
    setShowDetailModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this dealer? This action cannot be undone.')) {
      try {
        await dealerService.destroy(id);
        loadDealers();
      } catch (error) {
        console.error('Error deleting dealer:', error);
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingDealer(null);
    setFormData({ 
      name: '', 
      logo: '',
      email: '', 
      phone: '', 
      address: '', 
      city: '', 
      state: '', 
      postal_code: '', 
      latitude: '', 
      longitude: '', 
      website: '',
      commission_rate: '', 
      status: 'active',
      is_featured: false
    });
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setViewingDealer(null);
  };

  const filteredDealers = dealers.filter(d => 
    d.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.phone?.includes(searchQuery)
  );

  if (loading) return <div className="text-slate-400 animate-pulse">Synchronizing dealer network...</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">Dealer Network</h2>
          <p className="text-sm text-slate-500 mt-1">Manage and monitor your certified dealer partnerships.</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center justify-center gap-2 bg-red-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-red-500 transition-all shadow-lg shadow-red-900/20"
        >
          <Plus size={18} />
          Add New Dealer
        </button>
      </div>

      <div className="bg-slate-900 rounded-3xl border border-slate-800 overflow-hidden">
        <div className="p-4 border-b border-slate-800 bg-slate-900/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              className="w-full bg-slate-950 border border-slate-800 rounded-xl py-2.5 pl-10 pr-4 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-950/50">
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500">Dealer Info</th>
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500">Contact</th>
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500">Commission</th>
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500">Status</th>
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filteredDealers.map((dealer) => (
                <tr key={dealer.id} className="hover:bg-slate-800/30 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {dealer.logo ? (
                        <img src={dealer.logo} alt={dealer.name} className="h-10 w-10 rounded-xl object-cover border border-slate-700 bg-white" />
                      ) : (
                        <div className="h-10 w-10 rounded-xl bg-slate-800 flex items-center justify-center text-red-500 font-bold border border-slate-700">
                          {dealer.name?.charAt(0)}
                        </div>
                      )}
                      <div>
                        <div className="font-bold text-slate-200">{dealer.name}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                          <MapPin size={10} />
                          {dealer.city}, {dealer.state}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="text-sm text-slate-300 flex items-center gap-2">
                        <Mail size={12} className="text-slate-500" />
                        {dealer.email}
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-2">
                        <Phone size={12} className="text-slate-500" />
                        {dealer.phone || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 text-red-500 font-mono font-bold">
                      <Percent size={14} />
                      {dealer.commission_rate}%
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase ${
                      dealer.status === 'active' ? 'bg-red-500/10 text-red-500' : 'bg-red-500/10 text-red-500'
                    }`}>
                      {dealer.status === 'active' ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />}
                      {dealer.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleView(dealer)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
                        title="View Full Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => handleEdit(dealer)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
                        title="Edit Dealer"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(dealer.id)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
                        title="Delete Dealer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredDealers.length === 0 && (
            <div className="text-center py-20">
              <div className="bg-slate-800 w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-4 text-slate-600">
                <Users size={32} />
              </div>
              <p className="text-slate-500 font-medium">No dealers found matching your search.</p>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl w-full max-w-4xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="px-6 py-4 border-b border-slate-800 flex justify-between items-center bg-slate-900/50">
              <h3 className="text-lg font-bold text-slate-100">
                {editingDealer ? 'Edit Dealer Profile' : 'Register New Dealer'}
              </h3>
              <button onClick={closeModal} className="text-slate-500 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto max-h-[85vh]">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                {/* Branding & Tier */}
                <div className="md:col-span-4 space-y-6">
                  <div className="relative group mx-auto w-32">
                    <div className="h-32 w-32 rounded-[2rem] bg-slate-950 border-2 border-dashed border-slate-800 flex items-center justify-center overflow-hidden">
                      {formData.logo ? (
                        <img src={formData.logo} alt="Logo preview" className="h-full w-full object-cover" />
                      ) : (
                        <ImageIcon size={32} className="text-slate-700" />
                      )}
                      {uploading && (
                        <div className="absolute inset-0 bg-slate-950/80 flex items-center justify-center">
                          <div className="w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={handleLogoUpload}
                      accept="image/*"
                    />
                    <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mt-3 text-center">Company Logo</div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Promotion Tier</label>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, is_featured: !formData.is_featured })}
                      className={`w-full py-3 rounded-xl text-[10px] uppercase font-black tracking-widest border transition-all ${
                        formData.is_featured 
                          ? 'bg-amber-500/10 border-amber-500 text-amber-500 shadow-lg shadow-amber-900/10'
                          : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-700'
                      }`}
                    >
                      {formData.is_featured ? 'Premium Featured' : 'Standard Dealer'}
                    </button>
                  </div>
                </div>

                {/* Main Identity */}
                <div className="md:col-span-8 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Dealer / Company Name</label>
                      <input
                        type="text"
                        required
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g. John Doe Enterprises"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Official Website</label>
                      <div className="relative">
                        <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                        <input
                          type="url"
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 pl-12 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors font-mono"
                          value={formData.website}
                          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                          placeholder="https://company.com"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Business Email</label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                        <input
                          type="email"
                          required
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 pl-12 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors font-mono"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="billing@company.com"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Contact Number</label>
                      <div className="relative">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                        <input
                          type="tel"
                          required
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 pl-12 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors font-mono"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+1 (555) 000-0000"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-slate-800">
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">City</label>
                  <input
                    type="text"
                    required
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="e.g. Mumbai"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">State</label>
                  <input
                    type="text"
                    required
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="e.g. Maharashtra"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Postal Code</label>
                  <input
                    type="text"
                    required
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    placeholder="400001"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                <div className="md:col-span-8 space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Full Physical Address</label>
                  <textarea
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors resize-none"
                    rows={2}
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Street name, Landmark, ZIP..."
                  />
                </div>
                <div className="md:col-span-4 space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Commission Rate (%)</label>
                  <div className="relative">
                    <Percent className="absolute left-4 top-1/2 -translate-y-1/2 text-red-500" size={16} />
                    <input
                      type="number"
                      step="0.01"
                      required
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 pl-12 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors font-black"
                      value={formData.commission_rate}
                      onChange={(e) => setFormData({ ...formData, commission_rate: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4 p-6 bg-slate-950 rounded-2xl border border-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Navigation size={14} className="text-red-500" />
                    <label className="text-[10px] uppercase font-black tracking-widest text-red-500">Geospatial Detection</label>
                  </div>
                  {geocoding && <div className="text-[10px] text-red-400 animate-pulse font-bold uppercase">Detecting...</div>}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={handleGeocode}
                    disabled={geocoding}
                    className="flex items-center justify-center gap-3 bg-slate-900 text-slate-300 px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-800 hover:text-white transition-all border border-slate-800 disabled:opacity-50"
                  >
                    <Globe size={16} className="text-red-500" />
                    Resolve from Address
                  </button>
                  <button
                    type="button"
                    onClick={handleAutoLocate}
                    className="flex items-center justify-center gap-3 bg-slate-900 text-slate-300 px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-800 hover:text-white transition-all border border-slate-800"
                  >
                    <Navigation size={16} className="text-red-500" />
                    Get Current GPS
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Latitude</label>
                    <input
                      type="number"
                      step="any"
                      required
                      className="w-full bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors font-mono"
                      value={formData.latitude}
                      onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                      placeholder="0.00000000"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Longitude</label>
                    <input
                      type="number"
                      step="any"
                      required
                      className="w-full bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors font-mono"
                      value={formData.longitude}
                      onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                      placeholder="0.00000000"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5 pt-4 border-t border-slate-800">
                <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Account Lifecycle Status</label>
                <div className="flex gap-4">
                  {['active', 'suspended'].map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setFormData({ ...formData, status: s })}
                      className={`flex-1 py-3 rounded-xl text-[10px] uppercase font-black tracking-widest border transition-all ${
                        formData.status === s 
                          ? s === 'active' ? 'bg-red-500/10 border-red-500 text-red-500 shadow-lg shadow-red-900/10' : 'bg-red-500/10 border-red-500 text-red-500 shadow-lg shadow-red-900/10'
                          : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-700'
                      }`}
                    >
                      {s} Account
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-8 py-3 rounded-xl text-sm font-bold text-slate-400 hover:text-white transition-colors uppercase tracking-widest"
                >
                  Discard
                </button>
                <button
                  type="submit"
                  className="px-12 py-3 bg-red-600 text-white rounded-xl font-black uppercase tracking-widest hover:bg-red-500 transition-all shadow-lg shadow-red-900/20"
                >
                  {editingDealer ? 'Update Profile' : 'Confirm Dealer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showDetailModal && viewingDealer && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex items-center justify-center z-[110] p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="relative h-48 bg-gradient-to-br from-red-600 to-red-900">
              <button 
                onClick={closeDetailModal} 
                className="absolute top-6 right-6 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all z-10"
              >
                <X size={20} />
              </button>
              
              <div className="absolute -bottom-12 left-10">
                <div className="h-32 w-32 rounded-[2rem] bg-white p-1 shadow-2xl border-4 border-slate-900">
                  {viewingDealer.logo ? (
                    <img src={viewingDealer.logo} alt={viewingDealer.name} className="h-full w-full object-cover rounded-[1.75rem]" />
                  ) : (
                    <div className="h-full w-full bg-slate-100 rounded-[1.75rem] flex items-center justify-center text-red-600 text-4xl font-black">
                      {viewingDealer.name?.charAt(0)}
                    </div>
                  )}
                </div>
              </div>

              {viewingDealer.is_featured && (
                <div className="absolute top-10 left-10 px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white text-[10px] font-black uppercase tracking-[0.2em]">
                  Premium Certified Partner
                </div>
              )}
            </div>

            <div className="px-10 pt-16 pb-10 space-y-8">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-3xl font-black text-white">{viewingDealer.name}</h3>
                  <div className="flex items-center gap-2 mt-2 text-red-500 font-bold">
                    <MapPin size={16} />
                    <span>{viewingDealer.city}, {viewingDealer.state}</span>
                  </div>
                </div>
                {viewingDealer.website && (
                  <a 
                    href={viewingDealer.website.startsWith('http') ? viewingDealer.website : `https://${viewingDealer.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-red-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-red-500 transition-all text-sm shadow-lg shadow-red-900/20"
                  >
                    Visit Website
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>

              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-500 block mb-2">Location Address</label>
                    <p className="text-slate-300 text-sm leading-relaxed">{viewingDealer.address}, {viewingDealer.postal_code}</p>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-500 block mb-2">Network ID</label>
                    <p className="text-slate-300 font-mono text-xs uppercase">#ORT-DLR-{viewingDealer.id.toString().padStart(5, '0')}</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-500 block mb-2">Primary Contact</label>
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 text-slate-300 text-sm">
                        <Mail size={14} className="text-slate-500" />
                        {viewingDealer.email}
                      </div>
                      <div className="flex items-center gap-3 text-slate-300 text-sm">
                        <Phone size={14} className="text-slate-500" />
                        {viewingDealer.phone}
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-500 block mb-2">Commission Terms</label>
                    <div className="flex items-center gap-2 text-red-500 font-black text-xl">
                      <Percent size={18} />
                      {viewingDealer.commission_rate}%
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-slate-800 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${viewingDealer.status === 'active' ? 'bg-red-500 shadow-lg shadow-red-500/50' : 'bg-red-500'}`} />
                  <span className="text-[10px] uppercase font-black tracking-widest text-slate-400">Account {viewingDealer.status}</span>
                </div>
                <button 
                  onClick={() => { setShowDetailModal(false); handleEdit(viewingDealer); }}
                  className="text-slate-400 hover:text-white transition-colors text-xs font-bold"
                >
                  Edit Profile
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DealersManagement;

